import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Loader2, Upload, Users } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const BulkDialog = ({ open, onOpenChange, rendered, sender, mode, customHtml, onSent }) => {
  const [recipients, setRecipients] = useState([]);
  const [skipped, setSkipped] = useState(0);
  const [fileName, setFileName] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const parseFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const emailKey = rows.length
        ? Object.keys(rows[0]).find((k) => k.toLowerCase().includes("email")) ||
          Object.keys(rows[0]).find((k) => EMAIL_RE.test(String(rows[0][k]).trim()))
        : null;
      if (!emailKey) {
        toast.error("No email column found in the file");
        return;
      }
      const seen = new Set();
      const valid = [];
      let bad = 0;
      for (const row of rows) {
        const email = String(row[emailKey]).trim().toLowerCase();
        if (!EMAIL_RE.test(email)) { bad++; continue; }
        if (seen.has(email)) { bad++; continue; }
        seen.add(email);
        const fields = {};
        Object.entries(row).forEach(([k, v]) => {
          fields[k.trim().toLowerCase().replace(/\s+/g, "_")] = String(v);
        });
        valid.push({ email, fields });
        if (valid.length >= 500) break;
      }
      setRecipients(valid);
      setSkipped(bad);
      setFileName(file.name);
      if (!valid.length) toast.error("No valid email addresses in the file");
    } catch {
      toast.error("Could not read the file — use .csv or .xlsx");
    }
  };

  const onSend = async () => {
    setSending(true);
    try {
      let subject = rendered.subject;
      let html = customHtml;
      if (!html) {
        const tpl = await axios.get(`${API}/templates/${rendered.id}`, { params: { mode, content: "placeholder" } });
        html = tpl.data.html;
        subject = tpl.data.subject;
      }
      const res = await axios.post(`${API}/campaigns`, {
        template_id: rendered.id,
        subject,
        html,
        recipients,
      });
      toast.success(`Campaign started — sending to ${res.data.total} clients`);
      setRecipients([]); setFileName(""); setSkipped(0);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start campaign");
    } finally {
      setSending(false);
    }
  };

  const matchedVars = rendered?.placeholders?.filter(
    (p) => recipients[0]?.fields && p in recipients[0].fields
  ) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-normal">
            Bulk campaign <em className="text-accent">{rendered?.name}</em>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Upload a CSV or Excel file of clients. From: {sender?.from_name} &lt;{sender?.from_email}&gt;
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            data-testid="bulk-file-input"
            onChange={(e) => e.target.files[0] && parseFile(e.target.files[0])} />
          <button
            data-testid="bulk-upload-btn"
            onClick={() => fileRef.current?.click()}
            className="w-full flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-8 text-sm text-muted-foreground hover:border-accent/60 hover:text-foreground transition-colors"
          >
            <Upload size={20} />
            {fileName || "Choose .csv or .xlsx file (needs an email column)"}
          </button>
          {recipients.length > 0 && (
            <div data-testid="bulk-summary" className="rounded-lg border border-border bg-secondary/50 px-4 py-3 text-xs space-y-1.5">
              <p className="flex items-center gap-2 font-semibold text-foreground">
                <Users size={13} /> {recipients.length} valid recipient{recipients.length > 1 ? "s" : ""}
                {skipped > 0 && <span className="text-muted-foreground font-normal">· {skipped} skipped (invalid/duplicate)</span>}
              </p>
              <p className="text-muted-foreground truncate">
                {recipients.slice(0, 3).map((r) => r.email).join(", ")}{recipients.length > 3 ? "…" : ""}
              </p>
              {matchedVars.length > 0 && (
                <p className="text-accent">Personalized per client: {matchedVars.map((v) => `{{${v}}}`).join(", ")}</p>
              )}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Include matching columns in your file (e.g. first_name) and each client automatically gets a personalized
            email. Clients missing a value get a friendly fallback.
          </p>
          <button
            data-testid="bulk-send-btn"
            onClick={onSend}
            disabled={sending || !recipients.length}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
            {sending ? "Starting…" : `Send campaign to ${recipients.length || 0} clients`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
